import { Grid, UnstyledButton } from "@mantine/core";
import { FC, useState } from "react";
import Stack from "../Stack";
import Group from "../Group";
import Checkbox from "../Checkbox";
import TextInput from "../TextInput";
import Text from "../Text";
import { useTranslation } from "react-i18next";

type EditableCheckboxListProps<T> = {
  items: T[];
  getCheckboxLabel: (item: T) => string;
  isChecked: (item: T) => boolean; // controlled check state
  onCheckboxToggle: (item: T, checked: boolean) => void; // checkbox change handler
  getTextInputValue?: (item: T) => string | number; // editable field value
  getTextInputSuffix?: (item: T) => string | number;
  onTextInputChange?: (item: T, newValue: string) => void; // handle input change externally
  editableField?: keyof T; // name of the property to edit
  gridSpan: any;
  disabled?: boolean;
};

export function EditableCheckboxList<T>({
  items,
  getCheckboxLabel,
  isChecked,
  onCheckboxToggle,
  getTextInputValue,
  getTextInputSuffix,
  onTextInputChange,
  editableField,
  gridSpan,
  disabled,
}: EditableCheckboxListProps<T>) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const { t } = useTranslation();

  return (
    <Grid>
      {items.map((item, index) => (
        <Grid.Col span={gridSpan} key={index}>
          <Stack>
            <Text
              size="sm"
              color="secondary"
              data-testid={`editable-checkbox-label-${item}`}
            >{`+ ${getCheckboxLabel(item)}`}</Text>
            <Group gap="xs">
              <Checkbox
                disabled={disabled}
                data-testid={`editable-checkbox-${item}`}
                checked={isChecked(item)}
                onChange={(e) => onCheckboxToggle(item, e.currentTarget.checked)}
              />

              {editableField && editingIndex === index ? (
                <>
                  <TextInput
                    size="xs"
                    defaultValue={String(getTextInputValue?.(item) ?? "")}
                    onBlur={() => setEditingIndex(null)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setEditingIndex(null);
                    }}
                    onChange={(e) => onTextInputChange?.(item, e.target.value)}
                    style={{ width: 70 }}
                  />
                  <Text size="sm">{String(getTextInputSuffix?.(item) ?? "")}</Text>
                </>
              ) : editableField ? (
                <>
                  <Text size="sm">{String(getTextInputValue?.(item) ?? "")}</Text>
                  <Text size="sm">{String(getTextInputSuffix?.(item) ?? "")}</Text>
                  {disabled ? null : <TextCTA onClick={() => setEditingIndex(index)} text={t("actions:change")} />}
                </>
              ) : null}
            </Group>
          </Stack>
        </Grid.Col>
      ))}
    </Grid>
  );
}

export default EditableCheckboxList;

interface TextCTAProps {
  text: string;
  onClick?: () => void;
}
const TextCTA: FC<TextCTAProps> = ({ text, onClick }) => {
  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        color: "var(--color-text-secondary)",
        fontSize: "var(--text-size-14)",
      }}
    >
      {text}
    </UnstyledButton>
  );
};
